import {
  ALLOWED_EMIT_TYPES,
  DEFAULT_SECTION_LIMITS,
  type AiDynamicSectionConfig,
  type AiDynamicSectionLimits,
} from './aiDynamicSection';
import { AiDynamicSectionRubricChecklist } from './AiDynamicSectionRubricChecklist';
import { AiDynamicSectionRubricStaleNotice } from './AiDynamicSectionRubricStaleNotice';
import type { AiDynamicSectionAvailableQuestion } from './AiDynamicSectionSettings';

export interface AiDynamicSectionSummaryProps {
  config: AiDynamicSectionConfig;
  /** Same list `AiDynamicSectionSettings` uses to resolve a bound question id to its text
   *  (defect 1: a binding is shown to the author by text, never by a bare id). */
  availableQuestions?: AiDynamicSectionAvailableQuestion[];
  /** True once client-side polling for a `pending` rubric gave up waiting. */
  rubricPollTimedOut?: boolean;
  /** Regenerates the rubric — surfaced here (not just in the edit panel) when the backend marks
   *  the section `rubric_stale`: the read view is where an author actually looks. Same prop
   *  contract as `AiDynamicSectionSettings` — omit in a context that cannot perform the call. */
  onRegenerateRubric?: () => Promise<void>;
  /** Dismisses the staleness notice, telling the caller the researcher chose to keep the rubric. */
  onKeepRubric?: () => void;
}

const SECTION_LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';
const READONLY_BOX_CLASS = 'bg-gray-50 border border-gray-200 rounded-md p-3 min-h-[2.5rem]';
const VALUE_TEXT_CLASS = 'text-sm text-gray-900';
const EMPTY_TEXT_CLASS = 'text-sm text-gray-500 italic';

const NO_GOAL_TEXT = 'No goal set yet.';
const NO_PROBING_INSTRUCTIONS_TEXT = 'No probing instructions set.';
const NO_TOPICS_TEXT = 'No topics added yet.';
const NO_CONTEXT_BINDINGS_TEXT = 'Not linked to any earlier question.';

const EMIT_TYPE_LABELS: Record<(typeof ALLOWED_EMIT_TYPES)[number], string> = {
  open_text: 'Open text',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  rating_scale: 'Rating scale',
  matrix_rating: 'Matrix rating',
  percentage_allocation: 'Percentage allocation',
};

const PLATFORM_DEFAULT_LABEL = 'platform default';

/** Local to this file — same reasoning as `EMIT_TYPE_LABELS` above: `AiDynamicSectionSettings`
 *  keeps its own copy with the extra id/min fields an input needs, rather than the two sharing
 *  one abstraction across files. */
interface LimitFieldSpec {
  field: keyof AiDynamicSectionLimits;
  label: string;
  defaultValue: number;
}

const LIMIT_FIELDS: LimitFieldSpec[] = [
  {
    field: 'min_questions',
    label: 'Min questions',
    defaultValue: DEFAULT_SECTION_LIMITS.min_questions,
  },
  {
    field: 'max_questions',
    label: 'Max questions',
    defaultValue: DEFAULT_SECTION_LIMITS.max_questions,
  },
  {
    field: 'advisory_seconds',
    label: 'Advisory seconds',
    defaultValue: DEFAULT_SECTION_LIMITS.advisory_seconds,
  },
  {
    field: 'hard_ceiling_seconds',
    label: 'Hard ceiling seconds',
    defaultValue: DEFAULT_SECTION_LIMITS.hard_ceiling_seconds,
  },
];

/**
 * Read-only view of one `ai_dynamic_section` question's config — the same information
 * `AiDynamicSectionSettings` edits, laid out for reading. No text input, textarea, or select ever
 * appears: this is what an author sees for a question that is NOT currently being edited
 * (Problem 1 — "all configuration of question shall be visible even without edit mode"). To edit
 * anything — including a rubric point directly — the author opens the question for editing.
 *
 * One deliberate exception to "no buttons": when `config.rubric_stale` is true, this renders
 * `AiDynamicSectionRubricStaleNotice` with its regenerate/keep actions. The read view is where an
 * author actually looks, so the notice cannot live only in the edit panel — it is the SAME shared
 * component `AiDynamicSectionSettings` uses, not a second hand-written copy that could drift.
 *
 * Follows the same collapsed-field pattern the rest of `QuestionCard` already uses for other
 * question types (a `bg-gray-50` box holding plain text, with an italic "not set" fallback when
 * empty) rather than inventing a new one.
 */
export function AiDynamicSectionSummary({
  config,
  availableQuestions,
  rubricPollTimedOut,
  onRegenerateRubric,
  onKeepRubric,
}: AiDynamicSectionSummaryProps) {
  const boundQuestionTexts = config.context_bindings.question_ids.map(
    (id) => availableQuestions?.find((q) => q.id === id)?.text ?? id,
  );

  return (
    <div className="space-y-6">
      <div>
        <span className={SECTION_LABEL_CLASS}>Goal</span>
        <div className={READONLY_BOX_CLASS}>
          {config.goal ? (
            <p className={VALUE_TEXT_CLASS}>{config.goal}</p>
          ) : (
            <p className={EMPTY_TEXT_CLASS}>{NO_GOAL_TEXT}</p>
          )}
        </div>
      </div>

      <div>
        <span className={SECTION_LABEL_CLASS}>Probing instructions</span>
        <div className={READONLY_BOX_CLASS}>
          {config.probing_instructions ? (
            <p className={VALUE_TEXT_CLASS}>{config.probing_instructions}</p>
          ) : (
            <p className={EMPTY_TEXT_CLASS}>{NO_PROBING_INSTRUCTIONS_TEXT}</p>
          )}
        </div>
      </div>

      <div>
        <span className={SECTION_LABEL_CLASS}>Topics</span>
        {config.topics.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {config.topics.map((topic, index) => (
              <span
                key={`${topic}-${index}`}
                className="inline-flex items-center bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        ) : (
          <p className={EMPTY_TEXT_CLASS}>{NO_TOPICS_TEXT}</p>
        )}
      </div>

      <div>
        <span className={SECTION_LABEL_CLASS}>Rubric</span>
        {config.rubric_stale && (
          <AiDynamicSectionRubricStaleNotice
            onRegenerateRubric={onRegenerateRubric}
            onKeepRubric={onKeepRubric}
          />
        )}
        <AiDynamicSectionRubricChecklist
          rubric={config.rubric}
          rubricStatus={config.rubric_status}
          pollTimedOut={rubricPollTimedOut}
        />
      </div>

      <div>
        <span className={SECTION_LABEL_CLASS}>Context from earlier questions</span>
        {boundQuestionTexts.length > 0 ? (
          <ul className="list-disc list-inside space-y-1">
            {boundQuestionTexts.map((text, index) => (
              <li key={`${text}-${index}`} className={VALUE_TEXT_CLASS}>
                {text}
              </li>
            ))}
          </ul>
        ) : (
          <p className={EMPTY_TEXT_CLASS}>{NO_CONTEXT_BINDINGS_TEXT}</p>
        )}
      </div>

      {config.media_pool.length > 0 && (
        <div>
          <span className={SECTION_LABEL_CLASS}>Media pool</span>
          <div className="space-y-3">
            {config.media_pool.map((asset) => (
              <div key={asset.asset_id} className="border border-gray-200 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500 font-mono">{asset.s3_key}</p>
                <p className={VALUE_TEXT_CLASS}>
                  {asset.description || <span className={EMPTY_TEXT_CLASS}>No description</span>}
                </p>
                <p className={VALUE_TEXT_CLASS}>
                  {asset.relevance || <span className={EMPTY_TEXT_CLASS}>No relevance note</span>}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {LIMIT_FIELDS.map(({ field, label, defaultValue }) => {
          const value = config.limits[field];
          return (
            <div key={field}>
              <span className={SECTION_LABEL_CLASS}>{label}</span>
              <p className={VALUE_TEXT_CLASS}>
                {value !== null && value !== undefined ? (
                  value
                ) : (
                  <>
                    {defaultValue}{' '}
                    <span className={EMPTY_TEXT_CLASS}>({PLATFORM_DEFAULT_LABEL})</span>
                  </>
                )}
              </p>
            </div>
          );
        })}
      </div>

      <div>
        <span className={SECTION_LABEL_CLASS}>Question types the prober may ask</span>
        <div className="flex flex-wrap gap-2">
          {config.allowed_emit_types.map((type) => (
            <span
              key={type}
              className="inline-flex items-center bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full"
            >
              {EMIT_TYPE_LABELS[type as (typeof ALLOWED_EMIT_TYPES)[number]] ?? type}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
